import { IMAGE_ACCEPT, imageFileError, imageUploadError } from "./imageUploadErrors";
import { useEffect, useRef, useMemo } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import ResizableImage from "./ResizableImage";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import Heading from "@tiptap/extension-heading";
import TextAlign from "@tiptap/extension-text-align";
import FontSize from "tiptap-extension-font-size";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { createLowlight } from "lowlight";
import axios from "../../axiosConfig";

// ✅ Tables
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";

import "./tiptapStyles.css";

import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Divider,
  Flex,
  Select,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Input,
  Text,
  FormControl,
  FormLabel,
  useToast,
} from "@chakra-ui/react";

import {
  FaBold,
  FaBorderNone,
  FaImage,
  FaItalic,
  FaListUl,
  FaListOl,
  FaHeading,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaCode,
  FaTerminal,
  FaTrash,
  FaFillDrip,
  FaFont,
} from "react-icons/fa";

import {
  RiInsertColumnLeft,
  RiInsertColumnRight,
  RiDeleteColumn,
  RiInsertRowTop,
  RiInsertRowBottom,
  RiDeleteRow,
} from "react-icons/ri";
import { MdOutlineTableView } from "react-icons/md";
import { normalizeHtml } from "./tiptapSanitizers";

const fontFamilies = [
  { label: "Default", value: "" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
  { label: "Times New Roman", value: "'Times New Roman', serif" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
  { label: "Comic Sans MS", value: "'Comic Sans MS', cursive" },
  { label: "Playwrite AU QLD", value: "'Playwrite AU QLD', cursive" },
  { label: "Raleway", value: "'Raleway', sans-serif" },
  { label: "Roboto", value: "'Roboto Mono', sans-serif" },
  { label: "Montserrat", value: "'Montserrat', sans-serif" },
];

const fontSizes = [
  { label: "Default", value: "" },
  { label: "Extra Small", value: "10px" },
  { label: "Small", value: "12px" },
  { label: "Normal", value: "16px" },
  { label: "Large", value: "20px" },
  { label: "XL", value: "24px" },
  { label: "2XL", value: "28px" },
];

const ToolbarPopover = ({ label, button, children }) => (
  <Popover placement="bottom-start" isLazy lazyBehavior="keepMounted" gutter={8} usePortal={false}>
    <Tooltip label={label}>
      <Box>
        <PopoverTrigger>{button}</PopoverTrigger>
      </Box>
    </Tooltip>
    {children}
  </Popover>
);

const TipTapEditor = ({ content = "", onChange }) => {
  const toast = useToast();
  const fileInputRef = useRef(null);

  // ✅ Keep lowlight stable (avoid re-creating per render)
  const lowlight = useMemo(() => createLowlight(), []);

  // ✅ Prevent controlled-editor feedback loops (cursor jumping)
  const lastEmittedHtmlRef = useRef("");
  const isApplyingExternalContentRef = useRef(false);
  const latestContentRef = useRef(content);

  useEffect(() => {
    latestContentRef.current = content;
  }, [content]);

  // ✅ Keep your cell formatting attributes (bg + border)
  const CustomTableCell = TableCell.extend({
    addAttributes() {
      return {
        ...this.parent?.(),

        backgroundColor: {
          default: null,
          parseHTML: (element) => element.style.backgroundColor || null,
          renderHTML: (attributes) => {
            if (!attributes.backgroundColor) return {};
            return { style: `background-color: ${attributes.backgroundColor};` };
          },
        },

        borderColor: {
          default: null,
          parseHTML: (element) => element.style.borderColor || null,
          renderHTML: (attributes) =>
            attributes.borderColor ? { style: `border-color: ${attributes.borderColor};` } : {},
        },

        borderWidth: {
          default: null,
          parseHTML: (element) => element.style.borderWidth || null,
          renderHTML: (attributes) =>
            attributes.borderWidth ? { style: `border-width: ${attributes.borderWidth};` } : {},
        },

        borderStyle: {
          default: null,
          parseHTML: (element) => element.style.borderStyle || null,
          renderHTML: (attributes) =>
            attributes.borderStyle ? { style: `border-style: ${attributes.borderStyle};` } : {},
        },
      };
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImage,
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Heading.configure({ levels: [1, 2, 3] }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      CodeBlockLowlight.configure({ lowlight }),

      // ✅ Tables (order matters)
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      CustomTableCell,
    ],
    content,
    onUpdate: ({ editor }) => {
      // If we're applying external content, don't emit changes back up
      if (isApplyingExternalContentRef.current) return;

      const normalizedEditor = normalizeHtml(editor.getHTML());
      const normalizedProp = normalizeHtml(latestContentRef.current);

      if (normalizedEditor === normalizedProp) return;

      // Remember what we emitted, so we don't immediately setContent back into editor.
      lastEmittedHtmlRef.current = normalizedEditor;

      onChange?.(normalizedEditor);
    },
    editorProps: {
      attributes: {
        class: "tiptap-editor",
      },
    },
  });

  const { currentFontFamily, currentFontSize } = useEditorState({
    editor,
    selector: ({ editor }) => {
      const textStyle = editor?.getAttributes("textStyle") || {};
      return {
        currentFontFamily: textStyle.fontFamily || "",
        currentFontSize: textStyle.fontSize || "",
      };
    },
  });

  // ✅ Only apply external content if it did NOT come from this editor update
  useEffect(() => {
    if (!editor) return;
    const normalizedContent = normalizeHtml(content);

    // If parent is just reflecting what we emitted, do nothing (prevents cursor jumps)
    if (normalizedContent === lastEmittedHtmlRef.current) return;

    // If content is actually different (e.g. initial load, switching record), update editor
    isApplyingExternalContentRef.current = true;

    // emitUpdate=false prevents onUpdate firing from setContent
    editor.commands.setContent(normalizedContent, false);

    isApplyingExternalContentRef.current = false;
    lastEmittedHtmlRef.current = normalizedContent;
  }, [content, editor]);

  if (!editor) return null;

  /*--------------
    Helpers
  -------------*/

  const isInTable = editor.isActive("table");

  const insertTable = () =>
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();

  const setCellBg = (color) =>
    editor.chain().focus().setCellAttribute("backgroundColor", color).run();

  const setTextColor = (color) => editor.chain().focus().setColor(color).run();
  const unsetTextColor = () => editor.chain().focus().unsetColor().run();

  const setCellBorderColor = (color) =>
    editor.chain().focus().setCellAttribute("borderColor", color).run();

  const setCellBorderWidth = (width) =>
    editor.chain().focus().setCellAttribute("borderWidth", width).run();

  const setCellBorderStyle = (style) =>
    editor.chain().focus().setCellAttribute("borderStyle", style).run();

  const unsetFontSizeWithFallback = () => {
    const chain = editor.chain().focus();
    if (typeof chain.unsetFontSize === "function") {
      chain.unsetFontSize().run();
      return;
    }

    editor.chain().focus().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run();
  };

  const unsetFontFamilyWithFallback = () => {
    const chain = editor.chain().focus();
    if (typeof chain.unsetFontFamily === "function") {
      chain.unsetFontFamily().run();
      return;
    }

    editor.chain().focus().setMark("textStyle", { fontFamily: null }).removeEmptyTextStyle().run();
  };

  const onFontSizeChange = (value) => {
    if (!value) {
      unsetFontSizeWithFallback();
      return;
    }

    editor.chain().focus().setFontSize(value).run();
  };

  const onFontFamilyChange = (value) => {
    if (!value) {
      unsetFontFamilyWithFallback();
      return;
    }

    editor.chain().focus().setFontFamily(value).run();
  };

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append("file", file);

    const response = await axios.post("/editor_images", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return response.data; // { url: "..." }
  };

  const insertUploadedImage = async (file) => {
    const validationError = imageFileError(file);
    if (validationError) {
      toast({ title: "Upload failed", description: validationError, status: "error", isClosable: true });
      return;
    }
    try {
      const { url } = await uploadImage(file);
      editor.chain().focus().setImage({ src: url, alt: file.name, width: 100, align: "center" }).run();
    } catch (e) {
      toast({
        title: "Upload failed",
        description: imageUploadError(e),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const onPickImageClick = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await insertUploadedImage(file);
  };

  return (
    <Box border="1px solid" borderColor="gray.300" borderRadius="md" p={4}>
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        aria-label="Inline image"
        style={{ display: "none" }}
        onChange={onFileChange}
      />

      {/* Toolbar (sticky) */}
      <Box
        position="sticky"
        top="0"
        zIndex="10"
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        borderTopRadius="md"
        p={3}
      >
        <Flex flexWrap="wrap" gap={2} mb={4} align="center">
          <Select
            size="sm"
            width="120px"
            isRequired={false}
            required={false}
            value={currentFontSize}
            onChange={(e) => onFontSizeChange(e.target.value)}
          >
            {fontSizes.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </Select>

          <Select
            size="sm"
            width="160px"
            isRequired={false}
            required={false}
            value={currentFontFamily}
            onChange={(e) => onFontFamilyChange(e.target.value)}
          >
            {fontFamilies.map((font) => (
              <option key={font.value} value={font.value}>
                {font.label}
              </option>
            ))}
          </Select>

          <Divider orientation="vertical" height="1.5rem" />

          <ButtonGroup isAttached variant="outline" size="sm">
            <Tooltip label="Bold">
              <IconButton icon={<FaBold />} onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} />
            </Tooltip>

            <Tooltip label="Italic">
              <IconButton icon={<FaItalic />} onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} />
            </Tooltip>

            <ToolbarPopover
              label="Text color"
              button={<IconButton aria-label="Text color" icon={<FaFont />} size="sm" variant="outline" />}
            >
              <PopoverContent width="220px" zIndex="popover">
                <PopoverBody>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm">Text</Text>
                    <Input type="color" width="56px" padding="0" border="none" bg="transparent" required={false} onChange={(e) => setTextColor(e.target.value)} />
                  </HStack>

                  <Button size="sm" variant="outline" onClick={unsetTextColor} width="100%">
                    Reset text color
                  </Button>
                </PopoverBody>
              </PopoverContent>
            </ToolbarPopover>
          </ButtonGroup>

          <Divider orientation="vertical" height="1.5rem" />

          <ButtonGroup isAttached variant="outline" size="sm">
            <Tooltip label="Heading 1">
              <IconButton icon={<FaHeading />} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive("heading", { level: 1 })} />
            </Tooltip>

            <Tooltip label="Heading 2">
              <IconButton icon={<FaHeading style={{ fontSize: "0.8em" }} />} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive("heading", { level: 2 })} />
            </Tooltip>

            <Tooltip label="Heading 3">
              <IconButton icon={<FaHeading style={{ fontSize: "0.65em" }} />} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive("heading", { level: 3 })} />
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" height="1.5rem" />

          <ButtonGroup isAttached variant="outline" size="sm">
            <Tooltip label="Bullet List">
              <IconButton icon={<FaListUl />} onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive("bulletList")} />
            </Tooltip>

            <Tooltip label="Ordered List">
              <IconButton icon={<FaListOl />} onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive("orderedList")} />
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" height="1.5rem" />

          <ButtonGroup isAttached variant="outline" size="sm">
            <Tooltip label="Align Left">
              <IconButton icon={<FaAlignLeft />} onClick={() => editor.chain().focus().setTextAlign("left").run()} isActive={editor.isActive({ textAlign: "left" })} />
            </Tooltip>

            <Tooltip label="Align Center">
              <IconButton icon={<FaAlignCenter />} onClick={() => editor.chain().focus().setTextAlign("center").run()} isActive={editor.isActive({ textAlign: "center" })} />
            </Tooltip>

            <Tooltip label="Align Right">
              <IconButton icon={<FaAlignRight />} onClick={() => editor.chain().focus().setTextAlign("right").run()} isActive={editor.isActive({ textAlign: "right" })} />
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" height="1.5rem" />

          <ButtonGroup isAttached variant="outline" size="sm">
            <Tooltip label="Insert image">
              <IconButton aria-label="Insert image" icon={<FaImage />} onClick={onPickImageClick} />
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" height="1.5rem" />

          <ButtonGroup isAttached variant="outline" size="sm">
            <Tooltip label="Inline Code">
              <IconButton icon={<FaCode />} onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive("code")} />
            </Tooltip>

            <Tooltip label="Code Block">
              <IconButton icon={<FaTerminal />} onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive("codeBlock")} />
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" height="1.5rem" />

          {/* ✅ Tables */}
          <ButtonGroup isAttached variant="outline" size="sm">
            <Tooltip label="Insert table (3x3)">
              <IconButton aria-label="Insert table" icon={<MdOutlineTableView />} onClick={insertTable} />
            </Tooltip>

            {isInTable && (
              <>
                <Divider orientation="vertical" height="1.5rem" />

                <Tooltip label="Add column left">
                  <IconButton icon={<RiInsertColumnLeft />} onClick={() => editor.chain().focus().addColumnBefore().run()} />
                </Tooltip>

                <Tooltip label="Add column right">
                  <IconButton icon={<RiInsertColumnRight />} onClick={() => editor.chain().focus().addColumnAfter().run()} />
                </Tooltip>

                <Tooltip label="Delete column">
                  <IconButton icon={<RiDeleteColumn />} onClick={() => editor.chain().focus().deleteColumn().run()} />
                </Tooltip>

                <Divider orientation="vertical" height="1.5rem" />

                <Tooltip label="Add row above">
                  <IconButton icon={<RiInsertRowTop />} onClick={() => editor.chain().focus().addRowBefore().run()} />
                </Tooltip>

                <Tooltip label="Add row below">
                  <IconButton icon={<RiInsertRowBottom />} onClick={() => editor.chain().focus().addRowAfter().run()} />
                </Tooltip>

                <Tooltip label="Delete row">
                  <IconButton icon={<RiDeleteRow />} onClick={() => editor.chain().focus().deleteRow().run()} />
                </Tooltip>

                <Divider orientation="vertical" height="1.5rem" />

                <ToolbarPopover
                  label="Cell background"
                  button={<IconButton aria-label="Cell background" icon={<FaFillDrip />} size="sm" variant="outline" />}
                >
                  <PopoverContent width="220px" zIndex="popover">
                    <PopoverBody>
                      <HStack justify="space-between">
                        <Text fontSize="sm">Background</Text>
                        <Input type="color" width="56px" padding="0" border="none" bg="transparent" required={false} onChange={(e) => setCellBg(e.target.value)} />
                      </HStack>
                    </PopoverBody>
                  </PopoverContent>
                </ToolbarPopover>

                <ToolbarPopover
                  label="Cell border"
                  button={<IconButton aria-label="Cell border" icon={<FaBorderNone />} size="sm" variant="outline" />}
                >
                  <PopoverContent width="280px" zIndex="popover">
                    <PopoverBody>
                      <Text fontSize="sm" mb={3}>Cell Border</Text>

                      <FormControl mb={3}>
                        <HStack justify="space-between">
                          <FormLabel m="0" fontSize="sm">Color</FormLabel>
                          <Input type="color" width="56px" padding="0" border="none" bg="transparent" required={false} onChange={(e) => setCellBorderColor(e.target.value)} />
                        </HStack>
                      </FormControl>

                      <FormControl mb={3}>
                        <HStack justify="space-between">
                          <FormLabel m="0" fontSize="sm">Width</FormLabel>
                          <Select size="sm" width="120px" onChange={(e) => setCellBorderWidth(e.target.value)} placeholder="Default">
                            <option value="1px">1px</option>
                            <option value="2px">2px</option>
                            <option value="3px">3px</option>
                            <option value="4px">4px</option>
                          </Select>
                        </HStack>
                      </FormControl>

                      <FormControl>
                        <HStack justify="space-between">
                          <FormLabel m="0" fontSize="sm">Style</FormLabel>
                          <Select size="sm" width="120px" onChange={(e) => setCellBorderStyle(e.target.value)} placeholder="Default">
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                            <option value="double">Double</option>
                          </Select>
                        </HStack>
                      </FormControl>
                    </PopoverBody>
                  </PopoverContent>
                </ToolbarPopover>

                <Tooltip label="Delete table">
                  <IconButton icon={<FaTrash />} onClick={() => editor.chain().focus().deleteTable().run()} />
                </Tooltip>
              </>
            )}
          </ButtonGroup>
        </Flex>
      </Box>

      <Box p={4} maxH="420px" overflowY="auto">
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default TipTapEditor;
